import { useState } from "react";
import { toast } from "sonner";
import { Clock, Pencil } from "lucide-react";
import { errorMessage, patch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { isFutureRun } from "@/lib/constants";

const TZ = { timeZone: "Europe/London" };
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { ...TZ, hour: "2-digit", minute: "2-digit", hour12: false }) : "");
const day = (iso) => new Date(iso).toLocaleDateString("en-CA", TZ);

export const timeTaken = (s, e) => {
  const mins = Math.round((new Date(e) - new Date(s)) / 60000);
  return mins >= 60 ? `${Math.floor(mins / 60)} h ${mins % 60} min` : `${mins} min`;
};

const TimeEditor = ({ address, runDate, onSaved, onClose, tid, startedField, endedField }) => {
  const [start, setStart] = useState(clock(address[startedField]));
  const [end, setEnd] = useState(clock(address[endedField]));
  const [busy, setBusy] = useState(false);
  const at = (iso, hm) => (hm ? `${iso ? day(iso) : runDate}T${hm}` : null);

  const save = async () => {
    const s = at(address[startedField], start);
    const e = at(address[endedField] || address[startedField], end);
    if (s && e && e < s) {
      toast.error("The end time can't be before the start time");
      return;
    }
    setBusy(true);
    try {
      onSaved(await patch(`/addresses/${address.id}`, { [startedField]: s, [endedField]: e }));
      toast.success("Times updated");
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`${tid}-times-editor`}>
      <label className="text-xs font-medium text-slate-600">Start
        <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-9 w-32 bg-white" data-testid={`${tid}-start-time-input`} />
      </label>
      <label className="text-xs font-medium text-slate-600">End
        <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-9 w-32 bg-white" data-testid={`${tid}-end-time-input`} />
      </label>
      <Button size="sm" variant="ghost" onClick={onClose} disabled={busy} data-testid={`${tid}-times-cancel`}>Cancel</Button>
      <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={save} disabled={busy} data-testid={`${tid}-times-save`}>Save</Button>
    </div>
  );
};

export const AddressTimes = ({ address, runDate, onSaved, tid, role = "locksmith" }) => {
  const [editing, setEditing] = useState(false);
  const startedField = role === "locksmith" ? "started_at" : `${role}_started_at`;
  const endedField = role === "locksmith" ? "ended_at" : `${role}_ended_at`;
  const timeTid = role === "locksmith" ? tid : `${tid}-${role}`;
  const s = address[startedField];
  const e = address[endedField];
  if (isFutureRun(runDate)) return null;
  if (!s && !e) {
    return (
      <div className="mt-3">
        {!editing && (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)} className="h-8 px-2 text-xs text-slate-600" data-testid={`${timeTid}-times-add-button`}>
            <Clock className="mr-1.5 h-3.5 w-3.5" /> Add start and finish times
          </Button>
        )}
        {editing && <TimeEditor address={address} runDate={runDate} onSaved={onSaved} onClose={() => setEditing(false)} tid={timeTid} startedField={startedField} endedField={endedField} />}
      </div>
    );
  }
  const parts = [s ? `Started ${clock(s)}` : "Start: Not started", e && `Finished ${clock(e)}`].filter(Boolean);
  return (
    <div className="mt-3">
      <div className="font-data flex flex-wrap items-center gap-1.5 text-xs text-slate-600" data-testid={`${timeTid}-times`}>
        <Clock className="h-3.5 w-3.5 text-slate-400" />
        <span>{parts.join(" · ")}</span>
        {s && e && <span className="font-bold text-slate-900" data-testid={`${timeTid}-time-taken`}>· {timeTaken(s, e)}</span>}
        {!editing && (
          <Button size="icon" variant="ghost" onClick={() => setEditing(true)} aria-label="Edit times" className="h-7 w-7" data-testid={`${timeTid}-times-edit-button`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      {editing && <TimeEditor address={address} runDate={runDate} onSaved={onSaved} onClose={() => setEditing(false)} tid={timeTid} startedField={startedField} endedField={endedField} />}
    </div>
  );
};
